import { createFileRoute } from "@tanstack/react-router";
import { loadNewsArticle } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { resolveSiteUrl } from "~/utils/resolve-site-url";
import { BlogNewsletterSidebar } from "~/components/BlogNewsletterSidebar";
import { TableOfContents } from "~/components/TableOfContents";
import { AskAiSummary } from "~/components/AskAiSummary";
import type { NewsCardView } from "~/types/views";

export const Route = createFileRoute("/news/press-release/$slug")({
  loader: ({ params }) => loadNewsArticle({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.title,
          description: loaderData.seo_description || loaderData.description,
          image: loaderData.featured_image,
          url: `/news/press-release/${loaderData.slug}`,
          type: "article",
          publishedTime: loaderData.date,
          modifiedTime: loaderData.last_updated,
          authors: loaderData.authors,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/news/press-release/${loaderData.slug}` }) : [],
  }),
  component: PressRelease,
});

/* ------------------------------------------------------------------ */
/*  Press Release icon SVG                                             */
/* ------------------------------------------------------------------ */
function PressReleaseIcon() {
  return (
    <svg className="h-auto w-7 flex-none" width="26" height="27" viewBox="0 0 26 27" fill="none">
      <rect y="0.5" width="26" height="26" rx="4.10526" fill="#00343a" fillOpacity="0.6" />
      <mask id="mask0_pr" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="5" width="18" height="18">
        <rect x="4.44922" y="5.62891" width="16.6128" height="16.6123" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_pr)" />
      <path
        d="M7.47214 10.1005H6.11328V19.6125C6.11328 20.3599 6.72477 20.9714 7.47214 20.9714H16.9841V19.6125H7.47214V10.1005ZM18.343 7.38281H10.1898C9.44247 7.38281 8.83099 7.9943 8.83099 8.74167V16.8948C8.83099 17.6422 9.44247 18.2536 10.1898 18.2536H18.343C19.0903 18.2536 19.7018 17.6422 19.7018 16.8948V8.74167C19.7018 7.9943 19.0903 7.38281 18.343 7.38281ZM18.343 16.8948H10.1898V8.74167H18.343V16.8948ZM11.5487 12.1388H16.9841V13.4977H11.5487V12.1388ZM11.5487 14.1771H14.2664V15.5359H11.5487V14.1771ZM11.5487 10.1005H16.9841V11.4594H11.5487V10.1005Z"
        fill="#00343a"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Share badge SVGs (same as blog — exact Hugo match)                 */
/* ------------------------------------------------------------------ */
function LinkedInBadge() {
  return (
    <svg className="h-auto w-9" width="37" height="30" viewBox="0 0 37 30" fill="none">
      <rect y="0.5" width="37" height="29" rx="6" fill="white" />
      <rect y="0.5" width="37" height="29" rx="6" fill="#00343a" fillOpacity="0.1" />
      <path fillRule="evenodd" clipRule="evenodd" d="M27 23.5H23.507V17.5507C23.507 15.9196 22.8872 15.008 21.5962 15.008C20.1917 15.008 19.4579 15.9566 19.4579 17.5507V23.5H16.0917V12.1667H19.4579V13.6933C19.4579 13.6933 20.4701 11.8204 22.8751 11.8204C25.279 11.8204 27 13.2884 27 16.3244V23.5ZM12.0757 10.6827C10.9291 10.6827 10 9.74623 10 8.59133C10 7.43642 10.9291 6.5 12.0757 6.5C13.2224 6.5 14.1509 7.43642 14.1509 8.59133C14.1509 9.74623 13.2224 10.6827 12.0757 10.6827ZM10.3376 23.5H13.8477V12.1667H10.3376V23.5Z" fill="#00343a" />
    </svg>
  );
}

function XBadge() {
  return (
    <svg className="h-auto w-9" width="39" height="30" viewBox="0 0 39 30" fill="none">
      <rect y="0.5" width="38.2584" height="29" rx="6.44444" fill="white" />
      <rect y="0.5" width="38.2584" height="29" rx="6.44444" fill="#00343a" fillOpacity="0.1" />
      <path d="M10.3574 23.2581C10.4051 23.2018 10.4331 23.1676 10.4622 23.1342C12.5645 20.7314 14.667 18.3287 16.7694 15.9261C16.8577 15.8251 16.8623 15.7227 16.7831 15.6189C14.5535 12.7039 12.3241 9.78869 10.0947 6.87369C10.0672 6.83771 10.0403 6.80114 10 6.74717C10.0554 6.7446 10.0898 6.74144 10.124 6.74144C11.9738 6.74124 13.8236 6.74163 15.6734 6.73966C15.7475 6.73966 15.7926 6.76259 15.8371 6.8215C17.1201 8.52107 18.4049 10.2195 19.6896 11.9178C19.7127 11.9485 19.7374 11.9781 19.7669 12.0151C19.7985 11.9809 19.8232 11.9554 19.8467 11.9285C21.3312 10.2315 22.8159 8.53471 24.2984 6.83613C24.3575 6.76813 24.4166 6.73887 24.5079 6.73926C25.3505 6.74262 26.1931 6.74124 27.0359 6.74124H27.18C27.128 6.80351 27.0946 6.84542 27.0592 6.88575C25.0913 9.13491 23.1239 11.3845 21.1533 13.6315C21.0803 13.7147 21.0799 13.7639 21.1462 13.8513C23.4837 16.9363 25.8185 20.0237 28.1534 23.1107C28.1848 23.1522 28.2153 23.1941 28.2584 23.2524C28.205 23.255 28.1704 23.2579 28.136 23.2579C26.3356 23.2581 24.5352 23.2575 22.7348 23.2601C22.6524 23.2601 22.6044 23.2313 22.5561 23.168C21.1341 21.3053 19.7103 19.444 18.2865 17.5826C18.265 17.5543 18.2424 17.5266 18.2122 17.4885C18.18 17.5229 18.1533 17.5497 18.1284 17.5782C16.5004 19.4383 14.8722 21.2982 13.2461 23.1599C13.1844 23.2305 13.1238 23.2611 13.0283 23.2607C12.1857 23.2568 11.3431 23.2583 10.5004 23.2583C10.4624 23.2583 10.4243 23.2583 10.3572 23.2583L10.3574 23.2581ZM24.9472 21.5803C24.9051 21.5218 24.8832 21.4898 24.8598 21.4589C24.175 20.5533 23.4902 19.6479 22.8054 18.7424C20.2049 15.3039 17.6045 11.8659 15.006 8.42598C14.9518 8.35442 14.8959 8.3299 14.8089 8.3305C14.3384 8.33425 13.8677 8.33208 13.3972 8.33247C13.3634 8.33247 13.3294 8.33603 13.2738 8.339C13.3145 8.39336 13.3412 8.42954 13.3685 8.46513C14.3922 9.80351 15.4158 11.1417 16.4395 12.4799C18.7365 15.4832 21.0337 18.4866 23.3293 21.491C23.3779 21.5546 23.4277 21.5827 23.5094 21.5819C23.9437 21.5779 24.3783 21.5801 24.8126 21.5801H24.947L24.9472 21.5803Z" fill="#00343a" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Related News Cards                                                 */
/* ------------------------------------------------------------------ */
function RelatedNewsCards({ cards }: { cards: NewsCardView[] }) {
  if (cards.length === 0) return null;
  return (
    <div className="mx-auto max-w-screen-xl pb-24 pt-20 md:py-28">
      <h2 className="font-display text-28 font-bold text-charcoal md:text-33 lg:text-40">
        Explore other Press Releases
      </h2>
      <div className="mt-6 grid gap-5 md:mt-9 lg:grid-cols-3">
        {cards.map((card, i) => (
          <div
            key={i}
            className="group relative rounded-lg border border-[#DCDADA] bg-white px-7 py-6 shadow-md hover:shadow-lg lg:p-8"
          >
            <div className="relative">
              {card.image && (
                <img
                  src={card.image}
                  alt=""
                  className="-ml-1.5 mb-4 h-16 w-auto"
                  loading="lazy"
                />
              )}
              <h3 className="font-body font-bold text-charcoal sm:text-lg">
                {card.heading}
              </h3>
              {card.date && (
                <time className="mt-2 block font-body text-sm text-gray-60 transition duration-300 ease-in group-hover:text-charcoal">
                  {card.date}
                </time>
              )}
            </div>
            <a
              href={card.url}
              title={card.heading}
              className="absolute inset-0"
              {...(card.url.startsWith("http")
                ? { target: "_blank", rel: "noopener" }
                : {})}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Press Release Component                                       */
/* ------------------------------------------------------------------ */
function PressRelease() {
  const article = Route.useLoaderData();

  // Canonical origin, not the pre-rebrand domain — see blog/$slug.tsx.
  const permalink = `${resolveSiteUrl()}/news/press-release/${article.slug}`;
  const dateFormatted = new Date(article.date).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div itemScope itemType="http://schema.org/Article">
      <div className="relative">
        {/* ============ Hero Card ============ */}
        <div className="mt-10 px-4 md:px-8">
          <div className="mx-auto max-w-screen-xl">
            <div className="relative z-30 grid items-center overflow-hidden rounded-2xl border border-[#DCDADA] bg-white/90 shadow-card lg:grid-cols-2">
              {/* Left: text column */}
              <div className="p-5 lg:px-12 lg:py-10">
                {/* Press Release badge */}
                <div className="flex items-center space-x-3">
                  <PressReleaseIcon />
                  <span className="text-base text-gray-2">Press Release</span>
                </div>

                {/* Title */}
                <h1
                  className="mt-4 font-display text-28 font-bold leading-12 md:text-33 lg:text-40"
                  itemProp="headline name"
                >
                  {article.title}
                </h1>

                {/* Author + date */}
                <div className="mt-6 flex gap-6 text-sm">
                  <div className="flex items-center space-x-3">
                    {article.authors.length > 0 && (
                      <div className="flex items-center space-x-2">
                        {article.authors[0].image && (
                          <img
                            src={article.authors[0].image}
                            alt={article.authors[0].name}
                            className="w-9 rounded-full"
                            loading="lazy"
                          />
                        )}
                        <span>
                          {article.authors[0].name}
                          {article.authors[0].title
                            ? `, ${article.authors[0].title}`
                            : ""}
                        </span>
                      </div>
                    )}
                    <time
                      className="text-gray-500"
                      itemProp="datePublished dateModified"
                      content={new Date(article.date).toISOString().slice(0, 10)}
                    >
                      {dateFormatted}
                    </time>
                  </div>
                </div>

                {/* Share buttons */}
                <div className="mt-8 flex items-center space-x-3 text-teal-dark">
                  <p className="text-sm font-semibold">Share</p>
                  <ul className="flex items-center space-x-2">
                    <li>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(permalink)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkedInBadge />
                      </a>
                    </li>
                    <li>
                      <a
                        href={`https://twitter.com/share?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(permalink)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <XBadge />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: featured image */}
              <div className="block h-full overflow-hidden px-6 pb-8 pt-3 lg:rounded-l-xl lg:p-0">
                {article.featured_video ? (
                  <div className="relative" style={{ padding: "56.25% 0 0 0" }}>
                    <iframe
                      src={`${article.featured_video}&color=00343a&byline=0&portrait=0`}
                      className="absolute inset-0 h-full w-full"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : article.featured_image ? (
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    itemProp="image"
                    className={`rounded-md lg:h-full lg:object-cover ${
                      {
                        left: "lg:object-left-top",
                        right: "lg:object-right",
                        center: "lg:object-center",
                      }[article.featured_image_position ?? "left"]
                    }`}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {/* ============ Sidebar + Content ============ */}
          <div className="mx-auto mt-12 flex max-w-screen-xl flex-col md:flex-row md:gap-8 xl:gap-24">
            {/* Sidebar */}
            <div className="flex-none md:w-[17.18rem] md:pt-2">
              <div className="sticky md:top-28">
                <AskAiSummary url={`/news/press-release/${article.slug}`} className="mb-6 hidden md:block" />
                <TableOfContents entries={article.toc} />
                <div className="mt-8 hidden md:block">
                  <BlogNewsletterSidebar />
                </div>
              </div>
            </div>

            {/* Article body */}
            <div
              className="content flex-1 text-gray-1"
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: article.html }}
            />
          </div>

          {/* Mobile summary + newsletter */}
          <div className="mt-20 md:hidden">
            <AskAiSummary url={`/news/press-release/${article.slug}`} className="mb-6" />
            <BlogNewsletterSidebar />
          </div>

          {/* ============ Related News ============ */}
          <RelatedNewsCards cards={article.newsCards} />
        </div>
      </div>
    </div>
  );
}
